import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ENV } from "@/env";
import { isAdminUser } from "@/services/adminRoleService";

interface UseAuthGuardAdminResult {
  isAdmin: boolean;
  userEmail: string | null;
  loading: boolean;
}

export function useAuthGuardAdmin(): UseAuthGuardAdminResult {
  const navigate = useNavigate();
  const authDisabled = ENV.ADMIN_AUTH_DISABLED;
  const [isAdmin, setIsAdmin] = useState(authDisabled);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(!authDisabled);

  useEffect(() => {
    let active = true;

    if (authDisabled) {
      setIsAdmin(true);
      setLoading(false);
      return;
    }

    if (!supabase) {
      setLoading(false);
      navigate("/admin-login", { replace: true });
      return;
    }

    async function validate() {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error("Erro ao obter sessão do Supabase:", error);
      }

      const session = data?.session;

      if (!session?.user) {
        if (active) {
          setLoading(false);
          setIsAdmin(authDisabled);
          if (!authDisabled) {
            navigate("/admin-login", { replace: true });
          }
        }
        return;
      }

      if (active) {
        setUserEmail(session.user.email ?? null);
      }

      const hasAdminRole = await isAdminUser(session.user.id);

      if (!hasAdminRole && !authDisabled) {
        navigate("/admin-login", { replace: true });
      }

      if (active) {
        setIsAdmin(hasAdminRole);
        setLoading(false);
      }
    }

    validate();

    const {
      data: authListener,
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        if (!authDisabled) {
          navigate("/admin-login", { replace: true });
        }
        if (active) {
          setIsAdmin(authDisabled);
          setUserEmail(null);
          setLoading(false);
        }
        return;
      }

      setUserEmail(session.user.email ?? null);
      isAdminUser(session.user.id)
        .then(hasAdminRole => {
          if (!hasAdminRole && !authDisabled) {
            navigate("/admin-login", { replace: true });
          }

          if (active) {
            setIsAdmin(hasAdminRole);
            setLoading(false);
          }
        })
        .catch(error => {
          console.error("Erro ao verificar papel de admin:", error);
          if (active) {
            setIsAdmin(false);
            setLoading(false);
          }
        });
    });

    return () => {
      active = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [authDisabled, navigate]);

  return { isAdmin, userEmail, loading };
}

export default useAuthGuardAdmin;
